import { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subcontractRequestsApi, type SubcontractRequestFile } from '@/lib/apiClient';
import { fmtDateTime } from '@/lib/dates';
import { FILE_ACCEPT, REQUEST_FILES_MAX, sortUploads, carriesFiles, fmtSize } from './subcontractRequestModel';
import { saveBlob } from './saveBlob';

/**
 * Documents to include with a subcontract request (#656): optional extras (an exhibit, a scope sheet, a bond form) that the
 * coordinator downloads from the request. They are attached, not merged into the Word contract.
 *
 * Kept in the tracker's own database, so only the requester and the contracts coordinator can open them. THE SERVER DECIDES
 * WHO MAY CHANGE THEM: `canEdit` is the request's own `access.canEdit`, the same answer its Edit button uses, and the drop box
 * is offered only when it is true. Everyone who can read the request can download.
 *
 * Files go in through a drop box: drag one or more onto it, or click it to pick them. Several upload one after another, and
 * any that can't go are named. Removing asks first.
 */
export default function SubcontractRequestFiles({ requestId, canEdit }: { requestId: number; canEdit: boolean }) {
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /** "2 of 3" while a batch uploads, '' for a single file, else null. */
  const [progress, setProgress] = useState<string | null>(null);
  // Set synchronously, unlike state: a second drop that lands before React re-renders must still see that a batch is running.
  const runningRef = useRef(false);
  /** What a screen reader is told when something finishes: "2 documents attached.", "Document removed." */
  const [announce, setAnnounce] = useState('');
  // Under the request's own key, so everything that refreshes the request refreshes this too.
  const key = ['contracts', 'request', requestId, 'files'];

  const listQuery = useQuery({ queryKey: key, queryFn: () => subcontractRequestsApi.files(requestId) });
  const files = listQuery.data ?? [];
  const room = REQUEST_FILES_MAX - files.length;

  const upload = useMutation({ mutationFn: (file: File) => subcontractRequestsApi.uploadFile(requestId, file) });
  const busy = upload.isPending || progress !== null;
  const blocked = busy || listQuery.isError || room <= 0;

  /** Upload a batch one file at a time, reporting every file that didn't go. */
  const uploadAll = async (picked: File[]) => {
    if (picked.length === 0 || blocked || runningRef.current) return;
    runningRef.current = true;
    // The last batch's errors are about other files; clear them as this starts.
    setProblem(null);
    setAnnounce('');
    const { ok, problems } = sortUploads(picked, room);
    let attached = 0;
    try {
      for (let i = 0; i < ok.length; i++) {
        setProgress(ok.length > 1 ? `${i + 1} of ${ok.length}` : '');
        try {
          await upload.mutateAsync(ok[i]);
          attached += 1;
        } catch (err) {
          problems.push(`“${ok[i].name}”: ${(err as Error).message || 'Upload failed.'}`);
        }
      }
    } finally {
      runningRef.current = false;
      setProgress(null);
    }
    setProblem(problems.length ? problems.join('\n') : null);
    // Said aloud, for someone who cannot see the list change (the errors above are an alert of their own).
    if (attached > 0) setAnnounce(attached === 1 ? '1 document attached.' : `${attached} documents attached.`);
    void qc.invalidateQueries({ queryKey: key });
  };

  // A file dropped just OUTSIDE the box would make the browser open it in this tab, navigating away and losing the request.
  // While the box is on screen, the window ignores file drops; the box handles its own.
  useEffect(() => {
    if (!canEdit) return;
    const stop = (e: DragEvent) => { if (carriesFiles(e.dataTransfer)) e.preventDefault(); };
    window.addEventListener('dragover', stop);
    window.addEventListener('drop', stop);
    return () => {
      window.removeEventListener('dragover', stop);
      window.removeEventListener('drop', stop);
    };
  }, [canEdit]);

  const remove = useMutation({
    mutationFn: (fileId: number) => subcontractRequestsApi.removeFile(requestId, fileId),
    onSuccess: () => setAnnounce('Document removed.'),
    onSettled: () => qc.invalidateQueries({ queryKey: key }),
    onError: (err) => setProblem((err as Error).message || 'Couldn’t remove that document.'),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    // Reset so picking the same file again re-fires onChange.
    if (inputRef.current) inputRef.current.value = '';
    void uploadAll(picked);
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    void uploadAll(Array.from(e.dataTransfer.files ?? []));
  };

  const download = async (f: SubcontractRequestFile) => {
    setProblem(null);
    try {
      saveBlob(await subcontractRequestsApi.downloadFile(requestId, f.id), f.fileName);
    } catch (err) {
      setProblem((err as Error).message || `Couldn’t download “${f.fileName}”.`);
    }
  };

  return (
    <section className="ct-rq-section" aria-labelledby="rq-files">
      <h2 id="rq-files">Documents to include{files.length > 0 ? ` (${files.length})` : ''}</h2>
      <p className="ct-help">
        {canEdit
          ? `Optional. Anything the subcontract should go out with, such as an exhibit, a scope sheet or a bond form: up to ${REQUEST_FILES_MAX} files, 15 MB each. They are attached to this request, not added to the Word document. Kept in the Project Tracker, and visible only to the person who asked for the subcontract and the contracts coordinator.`
          : 'Attached to this request, not added to the Word document. Visible only to the person who asked for the subcontract and the contracts coordinator.'}
      </p>

      {canEdit && (
        <>
          <input ref={inputRef} type="file" multiple accept={FILE_ACCEPT} hidden aria-label="Choose documents to include" onChange={onPick} />
          {/* A real button, so the keyboard and screen readers can use it too: Enter or Space opens the file picker. */}
          <button
            type="button"
            className={`ct-drop${dragging ? ' ct-drop--over' : ''}`}
            data-testid="request-drop-zone"
            onClick={() => inputRef.current?.click()}
            // Only a drag carrying FILES lights the box. Selected text or a link dragged over it would drop to nothing.
            onDragEnter={(e) => {
              if (!carriesFiles(e.dataTransfer)) return;
              e.preventDefault();
              if (!blocked) setDragging(true);
            }}
            onDragOver={(e) => {
              if (!carriesFiles(e.dataTransfer)) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = blocked ? 'none' : 'copy';
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={onDrop}
            disabled={blocked}
          >
            <span className="ct-drop-title">
              {busy
                ? (progress ? `Uploading ${progress}…` : 'Uploading…')
                : room <= 0 ? `This request has ${REQUEST_FILES_MAX} documents, the most it can carry`
                  : dragging ? 'Drop to attach' : 'Drag files here, or click to choose'}
            </span>
            <span className="ct-drop-sub">PDF, Office, images or text, up to 15 MB each</span>
          </button>
        </>
      )}

      <span className="ct-sr" role="status">{announce}</span>
      {problem && <div className="ct-error" role="alert" style={{ whiteSpace: 'pre-line' }}>{problem}</div>}

      {listQuery.isPending ? (
        <p className="ct-help">Loading documents…</p>
      ) : listQuery.isError ? (
        <div className="ct-error" role="alert">{(listQuery.error as Error).message || 'The documents could not be loaded.'}</div>
      ) : files.length === 0 ? (
        <p className="ct-help">{canEdit ? 'No documents yet.' : 'No documents were attached.'}</p>
      ) : (
        <ul className="ct-files">
          {files.map((f) => {
            const who = f.uploadedBy?.displayName ?? f.uploadedBy?.email ?? 'Unknown';
            const size = fmtSize(f.sizeBytes);
            return (
              <li key={f.id} className="ct-file">
                <span className="ct-file-main">
                  <button type="button" className="ct-file-name" title="Download" onClick={() => void download(f)}>{f.fileName}</button>
                  <span className="ct-help">{who} · {fmtDateTime(f.uploadedAt)}{size ? ` · ${size}` : ''}</span>
                </span>
                {canEdit && (
                  <button
                    type="button" className="ct-file-x" aria-label={`Remove ${f.fileName}`} title="Remove this document"
                    disabled={remove.isPending}
                    onClick={() => {
                      if (window.confirm(`Remove “${f.fileName}” from this request?`)) {
                        setProblem(null);
                        remove.mutate(f.id);
                      }
                    }}
                  >
                    ×
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
